import { dateTimeFilterToBounds } from '@/shared/lib/dateTimeFilter';
import { getDataTableFilterValue } from '@/shared/lib/dataTableFilters';
import {
    ApiOperatorTaskBeforeDeadlineMode,
    ApiOperatorTaskOverdueMode,
    ApiOperatorTaskSource,
    ApiOperatorTaskType,
    ApiOperatorTaskView,
    type CreateOperatorTaskRequest,
    type OperatorTaskContextRequest,
    type OperatorTaskDetailsResponse,
    type OperatorTaskOptionsResponse,
    type OperatorTaskReminderSettingsRequest,
    type OperatorTaskReminderSettingsResponse,
    type OperatorTasksApiGetOperatorTasksRequest,
    type OperatorTasksApiGetOperatorTaskCalendarRequest,
    type OperatorTasksApiPreviewOperatorTaskRemindersRequest,
    type UpdateOperatorTaskRequest,
} from '@/core/generated/api';
import { isTaskChatRelated } from './model/chatContext';
import { calendarDateToDay, getMoscowCalendarParts, moscowCalendarToUtc } from '@/shared/lib/dateTime';
import type { OperatorTaskFormValues } from './model/schema';
import type { OperatorTaskFilters, OperatorTaskCalendarRange } from './model/types';
import type { OperatorTaskOrigin } from './model/types';
import { OPERATOR_TASK_CALENDAR_DEFAULT_PAGE_SIZE } from './model/calendarConstants';

/** Заполняет поля расписания напоминаний */
export const reminderSettingsToForm = (
    settings?: OperatorTaskReminderSettingsResponse,
    today = new Date()
): {
    atDeadline: boolean;
    dailyEnabled: boolean;
    dailyStartDate: Date;
    dailyTime: string;
    beforeEnabled: boolean;
    beforeMode: ApiOperatorTaskBeforeDeadlineMode;
    beforeTime: string;
    beforeOffset: number;
    overdueEnabled: boolean;
    overdueMode: ApiOperatorTaskOverdueMode;
    overdueHours: number;
    overdueTime: string;
} => ({
    atDeadline: settings?.atDeadline ?? true,
    dailyEnabled: Boolean(settings?.daily),
    dailyStartDate: settings?.daily?.startDate
        ? new Date(`${settings.daily.startDate}T00:00:00`)
        : today,
    dailyTime: settings?.daily?.time ?? '10:00',
    beforeEnabled: Boolean(settings?.beforeDeadline),
    beforeMode: settings?.beforeDeadline?.mode?.id ?? ApiOperatorTaskBeforeDeadlineMode.DeadlineDay,
    beforeTime: settings?.beforeDeadline?.time ?? '09:00',
    beforeOffset: settings?.beforeDeadline?.offsetMinutes ?? 60,
    overdueEnabled: Boolean(settings?.overdue),
    overdueMode: settings?.overdue?.mode?.id ?? ApiOperatorTaskOverdueMode.Hourly,
    overdueHours: settings?.overdue?.intervalHours ?? 1,
    overdueTime: settings?.overdue?.time ?? '10:00',
});

/** Заполняет форму создания или изменения задачи */
export const operatorTaskToForm = (
    options: OperatorTaskOptionsResponse,
    task?: OperatorTaskDetailsResponse,
    origin?: OperatorTaskOrigin
): OperatorTaskFormValues => {
    const today = getMoscowCalendarParts(options.serverTimeUtc ?? new Date().toISOString()).date;

    const due = task?.dueAtUtc
        ? getMoscowCalendarParts(task.dueAtUtc)
        : null;

    return {
        text: task?.text ?? '',
        type: task?.type?.id ?? ApiOperatorTaskType.OneTime,
        dueDate: due?.date ?? null,
        dueTime: due?.time ?? '',
        contextKind: task
            ? task.context?.chat && !task.context.order
                ? 'chat'
                : task.context?.order
                    ? 'order'
                    : 'none'
            : origin?.source === ApiOperatorTaskSource.Chat
                ? 'chat'
                : 'order',
        related: task?.context?.order ?? task?.context?.chat ?? origin?.related ?? null,
        assignee: task?.assignee ?? null,
        ...reminderSettingsToForm(task?.reminders ?? options.oneTimeDefaults, today),
    };
};

/** Собирает правила напоминаний из формы задачи */
export const formToReminderSettings = (
    values: OperatorTaskFormValues
): OperatorTaskReminderSettingsRequest => ({
    atDeadline: values.atDeadline,
    daily: values.type === ApiOperatorTaskType.Deadline && values.dailyEnabled && values.dailyStartDate
        ? {
            startDate: calendarDateToDay(values.dailyStartDate),
            time: values.dailyTime,
        }
        : undefined,
    beforeDeadline: values.type === ApiOperatorTaskType.Deadline && values.beforeEnabled
        ? {
            mode: values.beforeMode,
            time: values.beforeMode === ApiOperatorTaskBeforeDeadlineMode.DeadlineDay
                ? values.beforeTime
                : undefined,
            offsetMinutes: values.beforeMode === ApiOperatorTaskBeforeDeadlineMode.Offset
                ? values.beforeOffset
                : undefined,
        }
        : undefined,
    overdue: values.overdueEnabled
        ? {
            mode: values.overdueMode,
            intervalHours: values.overdueMode === ApiOperatorTaskOverdueMode.Hourly
                ? values.overdueHours
                : undefined,
            time: values.overdueMode === ApiOperatorTaskOverdueMode.Daily
                ? values.overdueTime
                : undefined,
        }
        : undefined,
});

/** Собирает выбранную связь задачи */
const formToContext = (values: OperatorTaskFormValues): OperatorTaskContextRequest | undefined => {
    if (values.contextKind === 'none') return {};

    const related = values.related;

    if (related?.availability?.id !== 'available') return undefined;
    const unsupportedChatRole = values.contextKind === 'chat' && !isTaskChatRelated(related);

    if (unsupportedChatRole) return undefined;

    return values.contextKind === 'order'
        ? { orderId: related.id ?? undefined }
        : {
            chat: {
                chatId: related.chatId,
                companionAppUserId: related.companionAppUserId ?? undefined,
                companionRole: related.companionRole?.id,
            },
        };
};

/** Собирает запрос создания задачи */
export const formToCreateTask = (
    values: OperatorTaskFormValues,
    clientRequestId: string,
    origin?: OperatorTaskOrigin
): CreateOperatorTaskRequest => ({
    text: values.text.trim(),
    type: values.type,
    dueAtUtc: values.dueDate
        ? moscowCalendarToUtc(values.dueDate, values.dueTime)
        : undefined,
    context: formToContext(origin
        ? {
            ...values,
            contextKind: origin.source === ApiOperatorTaskSource.Order
                ? 'order'
                : 'chat',
            related: origin.related,
        }
        : values),
    reminders: formToReminderSettings(values),
    clientRequestId,
    assignee: values.assignee?.id
        ? { appUserId: values.assignee.id }
        : undefined,
    createdFrom: origin?.source ?? ApiOperatorTaskSource.Section,
});

/** Собирает запрос изменения задачи */
export const formToUpdateTask = (
    values: OperatorTaskFormValues,
    original: OperatorTaskDetailsResponse
): UpdateOperatorTaskRequest => {
    const initial = original.context?.order ?? original.context?.chat;
    const sameContext = initial?.id === values.related?.id && initial?.chatId === values.related?.chatId;

    return {
        text: values.text.trim(),
        type: values.type,
        dueAtUtc: values.dueDate
            ? moscowCalendarToUtc(values.dueDate, values.dueTime)
            : undefined,
        context: sameContext
            ? undefined
            : formToContext(values),
        reminders: formToReminderSettings(values),
        version: original.version,
        assignee: original.assignee?.id === values.assignee?.id
            ? undefined
            : { appUserId: values.assignee?.id ?? null },
    };
};

/** Собирает запрос предпросмотра напоминаний */
export const formToPreview = (
    values: OperatorTaskFormValues,
    original?: OperatorTaskDetailsResponse
): OperatorTasksApiPreviewOperatorTaskRemindersRequest => {
    const reminders = formToReminderSettings(values);

    return {
        type: values.type,
        dueAtUtc: values.dueDate
            ? moscowCalendarToUtc(values.dueDate, values.dueTime)
            : undefined,
        operatorTaskId: original?.id,
        version: original?.version,
        atDeadline: reminders.atDeadline ?? false,
        dailyStartDate: reminders.daily?.startDate ?? undefined,
        dailyTime: reminders.daily?.time ?? undefined,
        beforeDeadlineMode: reminders.beforeDeadline?.mode,
        beforeDeadlineTime: reminders.beforeDeadline?.time ?? undefined,
        beforeDeadlineOffsetMinutes: reminders.beforeDeadline?.offsetMinutes ?? undefined,
        overdueMode: reminders.overdue?.mode,
        overdueIntervalHours: reminders.overdue?.intervalHours ?? undefined,
        overdueTime: reminders.overdue?.time ?? undefined,
    };
};

/** Собирает запрос выбранной очереди задач */
export const filtersToTaskQuery = (
    filters: OperatorTaskFilters
): OperatorTasksApiGetOperatorTasksRequest => {
    const nextDay = filters.date
        ? new Date(filters.date.getFullYear(), filters.date.getMonth(), filters.date.getDate() + 1)
        : null;

    return {
        nextReminderFromUtc: dateTimeFilterToBounds(getDataTableFilterValue(filters.tableFilters ?? {}, 'nextReminder'))?.from,
        nextReminderToUtc: dateTimeFilterToBounds(getDataTableFilterValue(filters.tableFilters ?? {}, 'nextReminder'))?.to,
        view: filters.view,
        orderId: filters.orderId ?? undefined,
        chatId: filters.chatId ?? undefined,
        assigneeId: filters.assignee?.id,
        types: filters.type === ApiOperatorTaskType.OneTime || filters.type === ApiOperatorTaskType.Deadline
            ? [filters.type]
            : undefined,
        searchQuery: filters.search.trim() || undefined,
        dueFromUtc: dateTimeFilterToBounds(getDataTableFilterValue(filters.tableFilters ?? {}, 'dueAtUtc'))?.from ?? (filters.date
            ? moscowCalendarToUtc(filters.date, '00:00')
            : undefined),
        dueToUtc: dateTimeFilterToBounds(getDataTableFilterValue(filters.tableFilters ?? {}, 'dueAtUtc'))?.to ?? (nextDay
            ? moscowCalendarToUtc(nextDay, '00:00')
            : undefined),
        ordering: filters.ordering,
        ascending: true,
        skipCount: filters.first,
        takeCount: filters.take,
    };
};

/** Выбирает задачи полного видимого интервала календаря */
export const filtersToTaskCalendarQuery = (
    filters: OperatorTaskFilters,
    range: OperatorTaskCalendarRange,
    options?: OperatorTaskOptionsResponse | null
): OperatorTasksApiGetOperatorTaskCalendarRequest => {
    const query = filtersToTaskQuery(filters);

    return {
        fromUtc: range.fromUtc,
        toUtc: range.toUtc,
        view: query.view,
        types: query.types,
        orderId: query.orderId,
        chatId: query.chatId,
        assigneeId: query.assigneeId,
        searchQuery: query.searchQuery,
        ascending: true,
        takeCount: options?.calendarMaxTake ?? OPERATOR_TASK_CALENDAR_DEFAULT_PAGE_SIZE,
    };
};

/** Подготавливает срок и напоминания переносимой задачи */
export const taskToRescheduleForm = (
    options: OperatorTaskOptionsResponse,
    task: OperatorTaskDetailsResponse,
    dueAtUtc: string,
    nowUtc: string
): OperatorTaskFormValues => {
    const values = operatorTaskToForm(options, task);
    const next = getMoscowCalendarParts(dueAtUtc);
    const today = getMoscowCalendarParts(nowUtc).date;

    return {
        ...values,
        dueDate: next.date,
        dueTime: next.time,
        dailyStartDate: values.dailyEnabled && values.dailyStartDate && calendarDateToDay(values.dailyStartDate) < calendarDateToDay(today)
            ? today
            : values.dailyStartDate,
    };
};

/** Различает открытые заказы и диалоги задачника */
export const getTaskOriginKey = (origin: OperatorTaskOrigin | null | undefined): string => {
    if (!origin) return '';
    if (origin.source === ApiOperatorTaskSource.Order && !origin.related.id) return '';

    const incompleteChatOrigin = origin.source === ApiOperatorTaskSource.Chat && !isTaskChatRelated(origin.related);

    if (incompleteChatOrigin) return '';

    return `${origin.source}:${origin.related.id ?? ''}:${origin.related.chatId ?? ''}:${origin.related.companionAppUserId ?? ''}:${origin.related.companionRole?.id ?? ''}`;
};

/** Выбирает задачи открытого заказа или диалога */
export const originToTaskQuery = (
    origin: OperatorTaskOrigin,
    first = 0,
    takeCount = 5
): OperatorTasksApiGetOperatorTasksRequest => ({
    orderId: origin.source === ApiOperatorTaskSource.Order
        ? origin.related.id ?? undefined
        : undefined,
    chatId: origin.source === ApiOperatorTaskSource.Chat
        ? origin.related.chatId ?? undefined
        : undefined,
    view: ApiOperatorTaskView.All,
    ordering: 'priority',
    ascending: true,
    skipCount: first,
    takeCount,
});
